/**
 * File API service.
 * Provides file system operations for sandboxes.
 *
 * API Endpoints:
 * - GET /sandboxes/:id/files?path= - Read file content
 * - GET /sandboxes/:id/files/list?path= - List directory contents
 * - GET /sandboxes/:id/files/stat?path= - Get file/directory info
 * - PUT /sandboxes/:id/files?path= - Write file content
 * - DELETE /sandboxes/:id/files?path= - Delete file/directory
 * - POST /sandboxes/:id/files/mkdir?path= - Create directory
 *
 * @module services/fileService
 */

import { httpClient } from './httpClient';
import type { FileListResponse, FileStatResponse } from '@/types';

const ENDPOINT = '/sandboxes';

/** Build base URL for a sandbox, using group-scoped path when sandboxGroupId is provided. */
function sandboxBase(sandboxId: string, sandboxGroupId?: string): string {
  const encodedId = encodeURIComponent(sandboxId);
  if (sandboxGroupId) {
    return `/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/sandboxes/${encodedId}`;
  }
  return `${ENDPOINT}/${encodedId}`;
}

/** Build containerName query param suffix */
function containerParam(containerName?: string): string {
  return containerName ? `&containerName=${encodeURIComponent(containerName)}` : '';
}

/**
 * File Service
 * Encapsulates all file system API operations for sandboxes
 */
export const fileService = {
  /**
   * List files in a directory
   */
  async listFiles(
    sandboxId: string,
    path: string,
    containerName?: string,
    sandboxGroupId?: string
  ): Promise<FileListResponse> {
    console.log('[FileService] Listing files:', sandboxId, 'Path:', path);
    const encodedPath = encodeURIComponent(path);
    try {
      const response = await httpClient.get<FileListResponse>(
        `${sandboxBase(sandboxId, sandboxGroupId)}/files/list?path=${encodedPath}${containerParam(containerName)}`
      );
      console.log('[FileService] Listed', response.data.entries?.length ?? 0, 'entries');
      return response.data;
    } catch (error) {
      console.error('[FileService] Failed to list files:', sandboxId, path, error);
      throw error;
    }
  },

  /**
   * Get file or directory info
   */
  async stat(
    sandboxId: string,
    path: string,
    containerName?: string,
    sandboxGroupId?: string
  ): Promise<FileStatResponse> {
    console.log('[FileService] Stat:', sandboxId, 'Path:', path);
    const encodedPath = encodeURIComponent(path);
    try {
      const response = await httpClient.get<FileStatResponse>(
        `${sandboxBase(sandboxId, sandboxGroupId)}/files/stat?path=${encodedPath}${containerParam(containerName)}`
      );
      console.log('[FileService] Stat result:', response.data);
      return response.data;
    } catch (error) {
      console.error('[FileService] Failed to stat:', sandboxId, path, error);
      throw error;
    }
  },

  /**
   * Read file content as blob
   */
  async readFile(
    sandboxId: string,
    path: string,
    containerName?: string,
    sandboxGroupId?: string
  ): Promise<Blob> {
    console.log('[FileService] Reading file:', sandboxId, 'Path:', path);
    const encodedPath = encodeURIComponent(path);
    try {
      const response = await httpClient.get(
        `${sandboxBase(sandboxId, sandboxGroupId)}/files?path=${encodedPath}${containerParam(containerName)}`,
        { responseType: 'blob' }
      );
      console.log('[FileService] Read file, size:', response.data.size);
      return response.data;
    } catch (error) {
      console.error('[FileService] Failed to read file:', sandboxId, path, error);
      throw error;
    }
  },

  /**
   * Write file content
   */
  async writeFile(
    sandboxId: string,
    path: string,
    content: Blob | File | string,
    options?: { createDirs?: boolean; mode?: number; containerName?: string },
    sandboxGroupId?: string
  ): Promise<void> {
    console.log('[FileService] Writing file:', sandboxId, 'Path:', path);

    const params = new URLSearchParams();
    params.append('path', path);
    if (options?.createDirs) {
      params.append('createDirs', 'true');
    }
    if (options?.mode) {
      params.append('mode', options.mode.toString());
    }
    if (options?.containerName) {
      params.append('containerName', options.containerName);
    }

    const blob =
      typeof content === 'string' ? new Blob([content], { type: 'text/plain' }) : content;

    try {
      await httpClient.put(
        `${sandboxBase(sandboxId, sandboxGroupId)}/files?${params.toString()}`,
        blob,
        {
          headers: { 'Content-Type': 'application/octet-stream' },
        }
      );
      console.log('[FileService] File written successfully');
    } catch (error) {
      console.error('[FileService] Failed to write file:', sandboxId, path, error);
      throw error;
    }
  },

  /**
   * Delete file or directory
   */
  async deleteFile(
    sandboxId: string,
    path: string,
    recursive: boolean = false,
    containerName?: string,
    sandboxGroupId?: string
  ): Promise<void> {
    console.log('[FileService] Deleting:', sandboxId, 'Path:', path, 'Recursive:', recursive);
    const encodedPath = encodeURIComponent(path);
    try {
      await httpClient.delete(
        `${sandboxBase(sandboxId, sandboxGroupId)}/files?path=${encodedPath}&recursive=${recursive}${containerParam(containerName)}`
      );
      console.log('[FileService] Deleted successfully');
    } catch (error) {
      console.error('[FileService] Failed to delete:', sandboxId, path, error);
      throw error;
    }
  },

  /**
   * Create a directory
   */
  async mkdir(
    sandboxId: string,
    path: string,
    containerName?: string,
    sandboxGroupId?: string
  ): Promise<void> {
    console.log('[FileService] Creating directory:', sandboxId, 'Path:', path);
    try {
      await httpClient.post(
        `${sandboxBase(sandboxId, sandboxGroupId)}/files/mkdir${containerName ? `?containerName=${encodeURIComponent(containerName)}` : ''}`,
        { path: path, createParents: true, mode: 0 }
      );
      console.log('[FileService] Directory created successfully');
    } catch (error) {
      console.error('[FileService] Failed to create directory:', sandboxId, path, error);
      throw error;
    }
  },
};
